import { Component } from '@theme/component';

const TEXTURE_WIDTH = 400;

let matterLoadPromise = null;

function loadMatterJs(src) {
  if (window.Matter) return Promise.resolve();
  if (matterLoadPromise) return matterLoadPromise;

  matterLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.onload = () => (window.Matter ? resolve() : reject(new Error('Matter.js loaded but did not define window.Matter')));
    script.onerror = () => reject(new Error('Failed to load Matter.js'));
    document.head.appendChild(script);
  });

  return matterLoadPromise;
}

/**
 * @typedef {object} Refs
 * @property {HTMLCanvasElement} canvas
 */

/** @extends {Component<Refs>} */
class InteractiveStickersComponent extends Component {
  requiredRefs = ['canvas'];

  #engine = null;
  #render = null;
  #runner = null;
  #walls = [];
  #stickerBodies = [];
  #isMobile = false;
  #started = false;
  #intersectionObserver = null;
  #resizeObserver = null;

  connectedCallback() {
    super.connectedCallback();
    this.#setupIntersectionObserver();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cleanup();
  }

  #setupIntersectionObserver() {
    this.#intersectionObserver = new IntersectionObserver(
      async (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !this.#started) {
            this.#started = true;
            this.#intersectionObserver.disconnect();
            try {
              await loadMatterJs(this.dataset.matterSrc);
            } catch {
              return;
            }
            this.#initPhysics();
          }
        }
      },
      { threshold: 0.1, rootMargin: '300px' }
    );
    this.#intersectionObserver.observe(this);
  }

  #getStickers() {
    const dataScript = this.querySelector('script[type="application/json"]');
    if (!dataScript) return [];
    try {
      return JSON.parse(dataScript.textContent).filter((s) => s.src);
    } catch {
      return [];
    }
  }

  #initPhysics() {
    const { Engine, Render, Runner, Bodies, Composite, MouseConstraint, Mouse } = Matter;

    const canvas = this.refs.canvas;
    const width = this.clientWidth;
    const height = this.clientHeight;

    canvas.width = width;
    canvas.height = height;

    this.#engine = Engine.create();
    this.#engine.gravity.y = 1.5;
    this.#engine.timing.timeScale = 0.85;

    this.#render = Render.create({
      canvas,
      engine: this.#engine,
      options: {
        width,
        height,
        wireframes: false,
        background: 'transparent',
        pixelRatio: Math.min(window.devicePixelRatio || 1, 2),
      },
    });

    this.#walls = this.#createWalls(width, height);
    Composite.add(this.#engine.world, this.#walls);

    this.#isMobile = width < 750;
    const stickers = this.#getStickers();
    const bodies = stickers.filter(sticker => !this.#isMobile || !sticker.mobileHidden).map((sticker, i) =>
      this.#createStickerBody(sticker, i, stickers.length, width)
    );
    this.#stickerBodies = bodies;
    Composite.add(this.#engine.world, bodies);

    const mouse = Mouse.create(canvas);
    // Remove wheel listeners that block page scrolling (not needed for drag)
    mouse.element.removeEventListener('mousewheel', mouse.mousewheel);
    mouse.element.removeEventListener('DOMMouseScroll', mouse.mousewheel);
    const mouseConstraint = MouseConstraint.create(this.#engine, {
      mouse,
      constraint: {
        stiffness: 0.2,
        render: { visible: false },
      },
    });
    Composite.add(this.#engine.world, mouseConstraint);
    this.#render.mouse = mouse;

    // Intercept touch events before Matter.js — only let them through when touching a sticker
    this.#setupTouchHandling(canvas);

    Render.run(this.#render);
    this.#runner = Runner.create();
    Runner.run(this.#runner, this.#engine);

    this.#resizeObserver = new ResizeObserver(() => this.#handleResize());
    this.#resizeObserver.observe(this);
  }

  #createWalls(width, height) {
    const { Bodies } = Matter;
    const t = 100;
    return [
      Bodies.rectangle(width / 2, height + t / 2, width * 3, t, {
        isStatic: true,
        render: { visible: false },
        label: 'wall-bottom',
      }),
      Bodies.rectangle(-t / 2, height / 2, t, height * 3, {
        isStatic: true,
        render: { visible: false },
        label: 'wall-left',
      }),
      Bodies.rectangle(width + t / 2, height / 2, t, height * 3, {
        isStatic: true,
        render: { visible: false },
        label: 'wall-right',
      }),
    ];
  }

  #createStickerBody(sticker, index, total, containerWidth) {
    const { Bodies } = Matter;

    const aspectRatio =
      sticker.naturalWidth > 0 && sticker.naturalHeight > 0
        ? sticker.naturalHeight / sticker.naturalWidth
        : 1;

    const isMobile = containerWidth < 750;
    const size = isMobile && sticker.mobileSize != null ? sticker.mobileSize : sticker.size;
    const bodyWidth = size;
    const bodyHeight = size * aspectRatio;
    const scale = size / TEXTURE_WIDTH;

    const usableWidth = Math.max(containerWidth - bodyWidth * 2, containerWidth * 0.6);
    const x =
      bodyWidth +
      (total > 1 ? (index / (total - 1)) * usableWidth : usableWidth / 2) +
      (Math.random() - 0.5) * 80;
    const y = -bodyHeight - index * 80 - Math.random() * 60;
    const angle = (Math.random() - 0.5) * 0.8;

    return Bodies.rectangle(x, y, bodyWidth, bodyHeight, {
      restitution: 0.3,
      friction: 0.8,
      frictionAir: 0.02,
      angle,
      render: {
        sprite: {
          texture: sticker.src,
          xScale: scale,
          yScale: scale,
        },
      },
    });
  }

  #handleResize() {
    if (!this.#engine || !this.#render) return;

    const { Body, Render, Composite } = Matter;
    const width = this.clientWidth;
    const height = this.clientHeight;
    const t = 100;

    // Update render dimensions — setPixelRatio handles canvas bitmap size,
    // canvas.style dimensions, and re-applies the devicePixelRatio scale transform.
    this.#render.options.width = width;
    this.#render.options.height = height;
    Render.setPixelRatio(this.#render, Math.min(window.devicePixelRatio || 1, 2));

    const [wallBottom, wallLeft, wallRight] = this.#walls;
    Body.setPosition(wallBottom, { x: width / 2, y: height + t / 2 });
    Body.setPosition(wallLeft, { x: -t / 2, y: height / 2 });
    Body.setPosition(wallRight, { x: width + t / 2, y: height / 2 });

    // Recreate sticker bodies when crossing the mobile breakpoint so that
    // mobileSize settings are applied correctly.
    const isMobile = width < 750;
    if (isMobile !== this.#isMobile) {
      this.#isMobile = isMobile;
      for (const body of this.#stickerBodies) {
        Composite.remove(this.#engine.world, body);
      }
      const stickers = this.#getStickers();
      this.#stickerBodies = stickers.filter(sticker => !this.#isMobile || !sticker.mobileHidden).map((sticker, i) =>
        this.#createStickerBody(sticker, i, stickers.length, width)
      );
      Composite.add(this.#engine.world, this.#stickerBodies);
    }
  }

  // canvas uses touch-action: pan-y so the browser owns vertical scroll natively.
  // We intercept at capture phase (before Matter.js bubble listeners):
  //   - touch on a sticker  → preventDefault() to cancel the pan gesture, let Matter.js drag
  //   - touch on empty space → stopImmediatePropagation() to block Matter.js, browser scrolls naturally
  #setupTouchHandling(canvas) {
    let touchingSticker = false;

    canvas.addEventListener(
      'touchstart',
      (e) => {
        const touch = e.touches[0];
        const rect = canvas.getBoundingClientRect();
        const pos = { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
        const movable = Matter.Composite.allBodies(this.#engine.world).filter((b) => !b.isStatic);
        touchingSticker = Matter.Query.point(movable, pos).length > 0;
        if (touchingSticker) {
          e.preventDefault(); // cancel pan-y so Matter.js can drag the sticker
        } else {
          e.stopImmediatePropagation(); // block Matter.js; browser handles scroll natively
        }
      },
      { capture: true, passive: false }
    );

    canvas.addEventListener(
      'touchmove',
      (e) => {
        if (touchingSticker) {
          e.preventDefault(); // keep blocking scroll for the duration of the drag
        } else {
          e.stopImmediatePropagation(); // block Matter.js mouse-move; browser scrolls natively
        }
      },
      { capture: true, passive: false }
    );

    canvas.addEventListener(
      'touchend',
      () => {
        touchingSticker = false;
      },
      { capture: true }
    );
  }

  #cleanup() {
    this.#intersectionObserver?.disconnect();
    this.#resizeObserver?.disconnect();

    if (this.#render) {
      Matter.Render.stop(this.#render);
      this.#render = null;
    }
    if (this.#runner) {
      Matter.Runner.stop(this.#runner);
      this.#runner = null;
    }
    if (this.#engine) {
      Matter.World.clear(this.#engine.world);
      Matter.Engine.clear(this.#engine);
      this.#engine = null;
    }
  }
}

if (!customElements.get('interactive-stickers-component')) {
  customElements.define('interactive-stickers-component', InteractiveStickersComponent);
}
